import { useEffect, useState, type CSSProperties } from 'react';
import { Icon, type SakhtarIconName } from '@sakhtar/design-system/react';
import type { AvatarPresetId } from './types/membership.js';

/**
 * The account's avatar, rendered the same way everywhere it appears — the
 * First Login Profile picker, the desktop topbar and its user menu, the
 * mobile bottom nav and the mobile menu drawer.
 *
 * Only the preset id (`a1`…`a10`) or the uploaded photo is stored
 * server-side (`OwnProfileResponse`); everything below is presentation.
 * Illustrated art is picked up from `assets/avatars/avatar-NN.jpg` by file
 * name, so adding or replacing a preset's art is a file drop — a preset
 * with no file yet falls back to its icon on its gradient.
 */

const AVATAR_ART: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(
    import.meta.glob<string>('../assets/avatars/avatar-*.jpg', { eager: true, import: 'default' }),
  ).flatMap(([path, url]) => {
    const n = /avatar-(\d+)\.jpg$/.exec(path)?.[1];
    return n ? [[`a${Number(n)}`, url]] : [];
  }),
);

export interface AvatarPresetDef {
  readonly id: AvatarPresetId;
  readonly c1: string;
  readonly c2: string;
  readonly icon: SakhtarIconName;
  /** Illustrated art when `assets/avatars` has a file for this preset. */
  readonly image?: string;
}

const PRESETS: readonly Omit<AvatarPresetDef, 'image'>[] = [
  { id: 'a1', c1: '#f3d86a', c2: '#e9c34a', icon: 'user' },
  { id: 'a2', c1: '#f2b3a3', c2: '#e39a88', icon: 'layers' },
  { id: 'a3', c1: '#cbbdf6', c2: '#ad9bec', icon: 'box' },
  { id: 'a4', c1: '#9cc3ee', c2: '#6f9fdb', icon: 'globe' },
  { id: 'a5', c1: '#bd82ee', c2: '#7233a8', icon: 'star' },
  { id: 'a6', c1: '#ee7ba2', c2: '#a82f5c', icon: 'shield' },
  { id: 'a7', c1: '#98a0b1', c2: '#4b5261', icon: 'trend' },
  { id: 'a8', c1: '#e9c665', c2: '#a8842a', icon: 'tag' },
  { id: 'a9', c1: '#7fc4b8', c2: '#2e8a7a', icon: 'building' },
  { id: 'a10', c1: '#f0a36b', c2: '#b8622c', icon: 'settings' },
];

export const AVATAR_PRESET_DEFS: readonly AvatarPresetDef[] = PRESETS.map((p) => {
  const image = AVATAR_ART[p.id];
  return image ? { ...p, image } : p;
});

export function avatarGradient(def: AvatarPresetDef): string {
  return `linear-gradient(158deg, ${def.c1}, ${def.c2})`;
}

export function avatarPresetDef(
  id: AvatarPresetId | null | undefined,
): AvatarPresetDef | undefined {
  return id ? AVATAR_PRESET_DEFS.find((a) => a.id === id) : undefined;
}

/** A preset's illustration when it has one, else its icon on the gradient. */
export function PresetFace({
  def,
  iconSize = 'lg',
}: {
  def: AvatarPresetDef;
  iconSize?: 'sm' | 'md' | 'lg';
}) {
  if (def.image) {
    return (
      <img
        src={def.image}
        alt=""
        draggable={false}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    );
  }
  return <Icon name={def.icon} size={iconSize} />;
}

export interface UserAvatarSource {
  readonly avatarPreset: AvatarPresetId | null;
  readonly avatarPhotoDataUrl: string | null;
}

/**
 * Photo, else preset, else the first letter of the name on the brand
 * gradient (`.sk-avatar`'s own look). `size` is the rendered diameter.
 */
export function UserAvatar({
  name,
  source,
  size,
  style,
  onPhotoError,
}: {
  name: string;
  source: UserAvatarSource | null;
  size: number;
  style?: CSSProperties;
  /** The photo URL failed to load (404, offline): the caller falls back to preset/initials. */
  onPhotoError?: () => void;
}) {
  const preset = avatarPresetDef(source?.avatarPreset);
  const photo = source?.avatarPhotoDataUrl ?? null;
  return (
    <span
      className="sk-avatar"
      aria-hidden
      style={{
        position: 'relative',
        overflow: 'hidden',
        flex: '0 0 auto',
        width: size,
        height: size,
        fontSize: Math.max(10, Math.round(size * 0.4)),
        ...(photo || preset ? { background: preset ? avatarGradient(preset) : 'none' } : {}),
        ...style,
      }}
    >
      {photo ? (
        <img
          src={photo}
          alt=""
          draggable={false}
          {...(onPhotoError ? { onError: onPhotoError } : {})}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : preset ? (
        <PresetFace def={preset} iconSize={size >= 40 ? 'md' : 'sm'} />
      ) : (
        name.trim().slice(0, 1)
      )}
    </span>
  );
}

/**
 * A person's avatar loaded from a URL (photo bytes served by an API endpoint).
 * While the photo loads or if it cannot be shown, the preset — or the first
 * letter of the name — takes its place, so a table row is never left blank
 * or broken.
 */
export function RemoteUserAvatar({
  name,
  photoUrl,
  preset,
  size = 28,
  style,
}: {
  name: string;
  photoUrl: string | null;
  preset?: AvatarPresetId | null;
  size?: number;
  style?: CSSProperties;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [photoUrl]);
  const photo = photoUrl !== null && !failed ? photoUrl : null;
  return (
    <UserAvatar
      name={name}
      size={size}
      {...(style ? { style } : {})}
      source={{ avatarPreset: preset ?? null, avatarPhotoDataUrl: photo }}
      onPhotoError={() => setFailed(true)}
    />
  );
}

/**
 * An organization's logo as its avatar: the image when there is one, else the
 * first letter of the name on the brand gradient. A rounded square with
 * `contain` so wide logos are not cropped.
 */
export function LogoAvatar({
  name,
  src,
  size = 32,
  style,
}: {
  name: string;
  src: string | null;
  size?: number;
  style?: CSSProperties;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const showImage = src !== null && !failed;
  return (
    <span
      className="sk-avatar"
      aria-hidden
      style={{
        position: 'relative',
        overflow: 'hidden',
        flex: '0 0 auto',
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.28),
        fontSize: Math.max(10, Math.round(size * 0.4)),
        ...(showImage
          ? { background: 'var(--sk-bg-elev, #fff)', border: '1px solid var(--sk-border)' }
          : {}),
        ...style,
      }}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          draggable={false}
          onError={() => setFailed(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            padding: Math.round(size * 0.08),
          }}
        />
      ) : (
        name.trim().slice(0, 1)
      )}
    </span>
  );
}
